import { useLottieJsonWithStatus } from 'hooks/useLottieJson';
import Lottie from 'lottie-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import clickWhite from '../../../assets/json/click_white.json';
import giftWhite3d from '../../../assets/json/gift_white_3d.json';
import raiseCup from '../../../assets/json/pricing-animate-1.json';
import throwInBasket from '../../../assets/json/pricing-animate-2.json';
import dribbling from '../../../assets/json/pricing-animate-3.json';
import './pricingTable.tailwind.css';

// CRA/webpack: no Next.js font loader
const montserrat = { className: '' } as const;

// TypeScript interfaces
interface FeatureData {
  title: string;
  description: string;
  icon: string;
  mediaType: 'image' | 'video';
  mediaUrl: string;
  mvp: boolean;
  allstar: boolean;
  rookie: boolean;
}

interface PlanConfig {
  name: string;
  lottie: string;
  color: string;
  bgColor: string;
  features: boolean[];
  bonuses: boolean[];
  pricing: {
    original?: number;
    sale: number;
    monthly?: number;
    installmentTotal?: number;
  };
  planId: string;
  badge?: string;
}

interface PlanConfigs {
  mvp: PlanConfig;
  allstar: PlanConfig;
  rookie: PlanConfig;
}

interface BonusConfig {
  name: string;
  delay?: number;
  durationDays?: number;
  hidden?: boolean;
  startDate?: string;
  scarcityMode?: 'default' | 'slots' | 'percentage-with-cooldown' | 'none';
  maxUnits?: number;
  finalLowUnitsThreshold?: number;
  cooldownLabel?: string;
}

interface NormalizedBonusConfig {
  name: string;
  delay: number;
  durationDays: number;
  hidden: boolean;
  startDate?: string;
  scarcityMode: 'default' | 'slots' | 'percentage-with-cooldown' | 'none';
  maxUnits?: number;
  finalLowUnitsThreshold?: number;
  cooldownLabel?: string;
}

type BonusClaimedColor = 'green' | 'yellow' | 'red';

interface ScarcityCurveSegment {
  endHour: number;
  minPct: number;
  maxPct: number;
}

interface ScarcityCurveSegmentCount {
  endHour: number;
  minValue: number;
  maxValue: number;
}

type LottieData = Record<string, any>;
type LottieSource = string | LottieData;

export interface LottieAnimations {
  mvp: LottieSource;
  allstar: LottieSource;
  rookie: LottieSource;
  thumb: LottieSource;
  gift: LottieSource;
}

interface HeaderConfig {
  title: string;
  subtitle: string;
  showBogoFeatureNote?: boolean;
}

export interface PricingTableProps {
  accentColor?: string;
  courseId?: string;
  directionId?: string;
  prices?: {
    purchase?: any;
    installment?: any;
    monthlySubscriptions?: any;
  };
  onPurchase?: (planId: string, paymentType: 'onetime' | 'plan') => void;
  customLogoUrl?: string;
  featuresData?: Record<string, FeatureData>;
  lottieAnimations?: Partial<LottieAnimations>;
  features?: string[];
  bonuses?: string[];
  bonusesConfig?: BonusConfig[];
  planConfigs?: Partial<PlanConfigs>;
  headerConfig?: HeaderConfig;
  courseName?: string;
  bundleCourseNames?: string[];
  pricingType?: 'certification' | 'study-material';
  isCertificate?: boolean;
  hasBronzeAccess?: boolean;
}

type PriceLike = {
  amount: number;
  defaultCoupon?: { discountAmount: number };
  firstMonthDiscountCoupon?: { discountAmount: number; discountType?: 'amount' | 'percentage' | string };
  [key: string]: any;
};

interface APIResponse {
  prices: {
    purchase: {
      gold: PriceLike;
      silver: PriceLike;
      bronze: PriceLike;
    };
    installment?: {
      gold: PriceLike;
      silver: PriceLike;
      bronze: PriceLike;
    };
    monthlySubscriptions?: {
      gold: PriceLike;
      silver: PriceLike;
      bronze: PriceLike;
    };
  };
  planFeatures?: {
    mvp?: boolean[];
    allstar?: boolean[];
    rookie?: boolean[];
  };
  planBonuses?: {
    mvp?: boolean[];
    allstar?: boolean[];
    rookie?: boolean[];
  };
}

interface BonusClaimedData {
  text: string;
  color: BonusClaimedColor;
  isMessage?: boolean;
}

const LIVE_BONUS_ANCHOR_UTC = new Date('2026-03-14T10:00:00Z');

const LIVE_BONUS_CURVE: ReadonlyArray<ScarcityCurveSegment> = [
  { endHour: 8, minPct: 50, maxPct: 70 },
  { endHour: 24, minPct: 70, maxPct: 80 },
  { endHour: 60, minPct: 80, maxPct: 90 },
  { endHour: 72, minPct: 91, maxPct: 98 },
];

const HOURS_PER_DAY = 24;
const BONUS_ACTIVE_DAYS = 7;
const BONUS_MIN_PERCENTAGE = 25;
const BONUS_MAX_PERCENTAGE = 98;
const LIVE_BONUS_ACTIVE_DAYS = 3;
const SLOT_BADGE_DEFAULT_MAX_UNITS = 50;
const SLOT_BADGE_DEFAULT_ACTIVE_DAYS = 3;
const SLOT_BADGE_DEFAULT_MAX_CLAIMED = 49;
const PERCENT_COOLDOWN_ACTIVE_HOURS = 96;
const PERCENT_COOLDOWN_HOURS = 24;
const PERCENT_COOLDOWN_CYCLE_SPAN_DAYS = (PERCENT_COOLDOWN_ACTIVE_HOURS + PERCENT_COOLDOWN_HOURS) / HOURS_PER_DAY;
const PERCENT_COOLDOWN_DEFAULT_LABEL = 'Limited-time partner offer';

const SLOT_BADGE_CURVE: ReadonlyArray<ScarcityCurveSegmentCount> = [
  { endHour: 6, minValue: 21, maxValue: 30 },
  { endHour: 18, minValue: 30, maxValue: 36 },
  { endHour: 36, minValue: 36, maxValue: 42 },
  { endHour: 60, minValue: 42, maxValue: 46 },
  { endHour: 72, minValue: 46, maxValue: 49 },
];

const PERCENT_COOLDOWN_CURVE: ReadonlyArray<ScarcityCurveSegment> = [
  { endHour: 8, minPct: 37, maxPct: 48 },
  { endHour: 24, minPct: 48, maxPct: 60 },
  { endHour: 48, minPct: 60, maxPct: 72 },
  { endHour: 72, minPct: 72, maxPct: 84 },
  { endHour: 88, minPct: 84, maxPct: 92 },
  { endHour: 96, minPct: 92, maxPct: 97 },
];

function normalizePositiveNumber(value: number | undefined, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return value;
}

function usesLiveBadge(bonusConfig: NormalizedBonusConfig): boolean {
  return bonusConfig.hidden !== true;
}

function getCycleSpanDays(durationDays: number): number {
  if (durationDays <= LIVE_BONUS_ACTIVE_DAYS) return durationDays;
  return durationDays + 1;
}

function getLiveBonusColor(percentage: number): BonusClaimedColor {
  if (percentage <= 70) return 'green';
  if (percentage <= 84) return 'yellow';
  return 'red';
}

function getBonusClaimedColor(percentage: number): BonusClaimedColor {
  if (percentage <= 35) return 'green';
  if (percentage <= 74) return 'yellow';
  return 'red';
}

function getSlotBadgeColor(claimedUnits: number): BonusClaimedColor {
  if (claimedUnits <= 36) return 'green';
  if (claimedUnits <= 45) return 'yellow';
  return 'red';
}

function getCycleElapsedHours(anchorDate: Date, currentDate: Date, delayHours: number, cycleSpanDays: number): number {
  const msElapsed = currentDate.getTime() - anchorDate.getTime();
  const baseHours = msElapsed / (1000 * 60 * 60);
  const shifted = baseHours + delayHours;
  const cycleSpanHours = cycleSpanDays * HOURS_PER_DAY;
  const cycle = shifted % cycleSpanHours;
  return cycle < 0 ? cycle + cycleSpanHours : cycle;
}

function calculateSegmentedPercentage(
  elapsedHours: number,
  curve: ReadonlyArray<ScarcityCurveSegment>,
  maxCap = BONUS_MAX_PERCENTAGE,
): number {
  let previousEnd = 0;
  for (const segment of curve) {
    if (elapsedHours <= segment.endHour) {
      const segmentDuration = Math.max(segment.endHour - previousEnd, 1);
      const segmentProgress = (elapsedHours - previousEnd) / segmentDuration;
      const interpolated = Math.round(segment.minPct + segmentProgress * (segment.maxPct - segment.minPct));
      return Math.min(Math.max(interpolated, segment.minPct), segment.maxPct, maxCap);
    }
    previousEnd = segment.endHour;
  }

  const fallback = curve[curve.length - 1];
  return Math.min(fallback.maxPct, maxCap);
}

function calculateSegmentedCount(
  elapsedHours: number,
  curve: ReadonlyArray<ScarcityCurveSegmentCount>,
  maxCap: number,
): number {
  let previousEnd = 0;
  for (const segment of curve) {
    if (elapsedHours <= segment.endHour) {
      const segmentDuration = Math.max(segment.endHour - previousEnd, 1);
      const segmentProgress = (elapsedHours - previousEnd) / segmentDuration;
      const interpolated = Math.round(segment.minValue + segmentProgress * (segment.maxValue - segment.minValue));
      return Math.min(Math.max(interpolated, segment.minValue), segment.maxValue, maxCap);
    }
    previousEnd = segment.endHour;
  }

  const fallback = curve[curve.length - 1];
  return Math.min(fallback.maxValue, maxCap);
}

function calculateBonusPercentage(
  startDate: Date,
  currentDate: Date,
  bonusConfig: NormalizedBonusConfig,
): number | null {
  const isShortWindowBonus = usesLiveBadge(bonusConfig);
  const delayHours = Number.isFinite(bonusConfig.delay) ? bonusConfig.delay : 0;
  const activeDays = normalizePositiveNumber(bonusConfig.durationDays, BONUS_ACTIVE_DAYS);
  const cycleSpanDays = getCycleSpanDays(activeDays);
  const activeHours = activeDays * HOURS_PER_DAY;

  const elapsedHours = getCycleElapsedHours(startDate, currentDate, delayHours, cycleSpanDays);

  if (!usesLiveBadge(bonusConfig) && elapsedHours > activeHours) {
    return null;
  }

  const activeCycleHours = Math.min(elapsedHours, activeHours);
  const curve: ReadonlyArray<ScarcityCurveSegment> = isShortWindowBonus
    ? LIVE_BONUS_CURVE
    : [
        {
          endHour: activeHours,
          minPct: BONUS_MIN_PERCENTAGE,
          maxPct: BONUS_MAX_PERCENTAGE,
        },
      ];

  return calculateSegmentedPercentage(activeCycleHours, curve, BONUS_MAX_PERCENTAGE);
}

// Default feature data (can be overridden via props)
const essentialCheatSheetFeature: FeatureData = {
  title: 'Essential cheat sheets',
  description:
    "Quickly grasp key concepts with our cheat sheet, perfect for efficient study and last-minute reviews, ensuring you're well-prepared with less time and effort.",
  icon: 'https://traineracademy.org/wp-content/uploads/2024/03/feat-thirteen.svg',
  mediaType: 'video',
  mediaUrl:
    'https://traineracademy.org/wp-content/uploads/2024/12/iptacpthomepagedesktopcheatsheet-ezgif.com-resize-video-1.mp4',
  mvp: true,
  allstar: false,
  rookie: false,
};

const defaultFeatureData: Record<string, FeatureData> = {
  'Exam + Cert': {
    title: 'NCCA Accredited Cert',
    description:
      'The IPTA CPT certification is 100% online and NCCA accredited, meeting top industry standards. Preferred by over 10,000 gyms, it ensures credibility and a competitive edge in your fitness career.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/12/NCCA.svg',
    mediaType: 'image',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/12/NCCA-accredited-certification.png',
    mvp: true,
    allstar: true,
    rookie: true,
  },
  'Free Digital Textbook': {
    title: 'Free Digital Textbook',
    description:
      "All students (even those on trial) get free access to the full IPTA CPT textbook—based on our results-first training model. Available on Kindle, Apple Books, PDF, or directly on our website, it's designed to help you succeed. Unlike other organizations that charge $100 or more, you can access it for free—even before signing up, whether for certification or personal trainer knowledge.",
    icon: 'https://traineracademy.org/wp-content/uploads/2024/12/Group-40.svg',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/12/desktopfreetextbookipta.mp4',
    mvp: true,
    allstar: true,
    rookie: true,
  },
  'Interactive study guide': {
    title: 'Interactive study guide',
    description:
      'Streamline your exam prep with our Interactive Study Guide, crafted to efficiently cut down your study time and bolster confidence with engaging, personalized content. Highlight, take notes and more.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/12/Group-82.svg',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/10/Studyguide.mp4',
    mvp: true,
    allstar: true,
    rookie: true,
  },
  'Exam Simulator': {
    title: 'Exam Simulator',
    description:
      'With over 800 exam questions, our exam simulator offers a real-test atmosphere, significantly reducing your study effort by acclimatizing you to the exam format, thus boosting your confidence and exam readiness. In our tutor mode, choose questions from a specific chapter, marked question, unused questions or questions you have previously gotten wrong to hyper focus on the sections you need to learn the most.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/03/feat-two.svg',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/10/practicetests.mp4',
    mvp: true,
    allstar: true,
    rookie: false,
  },
  'Flashcard system': {
    title: 'Flashcard system',
    description:
      'Master critical content faster with spaced repetition technology with over 500 flashcards. Our flashcards are designed for long-term retention and quick refreshers, supporting both comprehensive study and efficient cramming.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/03/feat-three.svg',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/10/flashcards.mp4',
    mvp: true,
    allstar: true,
    rookie: false,
  },
  'First Recert is Free': {
    title: 'First Recert Is Free',
    description:
      'Focus on your career, not recertification costs. With IPTA, your first recertification is free after 2 years, keeping you certified for 4 years. This saves you over $200 compared to other organizations and ensures no added financial burden after your initial certification.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/12/Group.svg',
    mediaType: 'image',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/12/first-recert.png',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'Job Assurance Guarantee': {
    title: 'Job Assurance Guarantee',
    description:
      "We're confident in our program and your potential. That's why we offer a Job Placement Guarantee for U.S. students who meet simple qualifications. If you don't secure a personal training job within six months of passing your exam, we'll refund the cost of the Job Assurance Guarantee.",
    icon: 'https://traineracademy.org/wp-content/uploads/2024/12/handshake.svg',
    mediaType: 'image',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/12/job-assurance.png',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'Unlimited Exam Retakes': {
    title: 'Unlimited Exam Retakes',
    description:
      "With our Exam Pass Guarantee, we pledge your success: complete our program with confidence, and if you don't pass, we'll refund your investment plus the exam retake fee.",
    icon: 'https://traineracademy.org/wp-content/uploads/2024/03/feat-four.svg',
    mediaType: 'image',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/12/passnew-desktop.png',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'SurePass AI': {
    title: 'SurePass AI',
    description:
      'SurePass AI evaluates your readiness, offering tailored feedback to ensure you approach your exam with confidence and a clear strategy for success. We will tell you the exact moment that you are ready to take the exam so that you do not risk failing.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/12/Group-55.svg',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/10/surepass.mp4',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'Audio study guide': {
    title: 'Audio study guide',
    description:
      'Maximize your study flexibility with our Audio Study Guide, allowing you to absorb material during downtime or when you are on the go, effectively reducing your overall study time.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/03/feat-six.svg',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/10/audiostudyguide.mp4',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'Performance reporting': {
    title: 'Performance reporting',
    description:
      'Get precise insights into your preparation with our Performance Reporting, focusing your efforts on areas needing improvement and efficiently streamlining your study path.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/03/feat-seven.svg',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/10/performancereporting.mp4',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'Study streaks': {
    title: 'Study streaks',
    description:
      'Our motivating study streaks incentivize consistent study habits, reducing effort by keeping you engaged and steadily progressing towards your exam goals.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/03/feat-eight.svg',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/10/Studystreaklms1024x576.mp4',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'Study gamification': {
    title: 'Study gamification',
    description:
      'Transform your study sessions with gamification, making learning enjoyable and reducing the perceived effort needed to grasp challenging material.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/03/feat-nine.svg',
    mediaType: 'image',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/10/study-gamification.png',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'Weak point training': {
    title: 'Weak point training',
    description:
      'Our Weak Area Training focuses your studies on areas needing the most attention, efficiently reducing both study time and effort by targeting your preparation.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/03/feat-ten.svg',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/10/weakareatraining.mp4',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'Tailored study plans': {
    title: 'Tailored study plans',
    description:
      "Optimize your exam prep with Tailored Study Plans, catering to both cramming and long-term study. Adapted to fit your pace, whether you're quickly absorbing material or taking a gradual approach.",
    icon: 'https://traineracademy.org/wp-content/uploads/2024/03/feat-eleven.svg',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/10/tailoredstudyplans.mp4',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'Magic mnemonics': {
    title: 'Magic mnemonics',
    description:
      'Enhance memory and reduce study time with mnemonic techniques, making complex information easy to remember, ideal for thorough preparation and effective cramming alike.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/03/feat-twelve.svg',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/10/magicmnemonics.mp4',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'Exam date tracker': {
    title: 'Exam date tracker',
    description:
      "Keep your study schedule on track with our Exam Date Tracker, ensuring you're adequately prepared when your exam date arrives, without the need for last-minute cramming.",
    icon: 'https://traineracademy.org/wp-content/uploads/2024/03/feat-thirteen.svg',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/10/examdatetracker.mp4',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  '0% interest financing': {
    title: '0% interest financing',
    description:
      'Make your dream of becoming a certified personal trainer a reality with our 0% interest financing. Spread the cost over 12 equal, manageable monthly payments—with no credit checks and 100% approval.',
    icon: 'https://traineracademy.org/wp-content/uploads/2024/12/Icon-1.svg',
    mediaType: 'image',
    mediaUrl: 'https://traineracademy.org/wp-content/uploads/2024/12/0-interest.png',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'CPR/AED Certification': {
    title: 'CPR/AED Certification',
    description:
      'MVP enrollees receive limited-time free access to our online CPR/AED certification through Trainer Academy—a $99 value—fulfilling the requirement to take your CPT exam at no extra cost.',
    icon: 'https://traineracademy.org/red-a.png',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/ipta_cpr_video_small.mp4',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'Trainer Stack All-Access': {
    title: 'Trainer Stack All-Access Business Courses',
    description:
      "Most certifications teach you the skills. <a href='https://www.trainerstack.co/' target='_blank' class='text-[#ED5A26] underline hover:text-[#d14010]'>Trainer Stack</a> teaches you how to turn them into income. As part of a limited collaboration, our BOGO MVP students receive free access to <a href='https://www.trainerstack.co/' target='_blank' class='text-[#ED5A26] underline hover:text-[#d14010]'>Trainer Stack</a> All-Access, including personal training, nutrition coaching, and business systems courses (valued at $799). Available to IPTA MVP purchasers only while the <a href='https://www.trainerstack.co/' target='_blank' class='text-[#ED5A26] underline hover:text-[#d14010]'>Trainer Stack</a> enrollment period is open.",
    icon: 'https://traineracademy.org/trainer-stack-icon.png',
    mediaType: 'video',
    mediaUrl: 'https://traineracademy.org/all_access_mp4.mp4',
    mvp: true,
    allstar: false,
    rookie: false,
  },
  'Essential cheat sheet': essentialCheatSheetFeature,
  'Essential cheat sheets': essentialCheatSheetFeature,
};

// Default Lottie animation URLs (can be overridden via props)
export const getDefaultLottieAnimations = (): LottieAnimations => {
  return {
    mvp: raiseCup,
    allstar: throwInBasket,
    rookie: dribbling,
    thumb: clickWhite,
    gift: giftWhite3d,
  };
};

// TypeScript interface for LottiePlayer props
interface LottiePlayerProps {
  src: string | LottieData;
  width?: number;
  height?: number;
  lottieAnimations?: LottieAnimations;
}

// Lottie animation player component
function LottiePlayer({ src, width = 120, height = 120, lottieAnimations }: LottiePlayerProps) {
  const resolved: LottieSource =
    typeof src === 'string' && (lottieAnimations as any)?.[src] ? (lottieAnimations as any)[src] : (src as any);

  const url = typeof resolved === 'string' ? resolved : '';
  const { data, status } = useLottieJsonWithStatus(url);

  const animationData: any = url ? (status === 'loaded' ? data : null) : resolved;

  if (!animationData) {
    return (
      <div style={{ width: `${width}px`, height: `${height}px` }} className="flex items-center justify-center">
        <div className="rounded-full h-8 w-8 border-b-2 border-orange-500 animate-spin" />
      </div>
    );
  }

  return <Lottie animationData={animationData} style={{ width, height, margin: '0 auto' }} loop autoplay />;
}

// Default features list (can be overridden via props)
const defaultFeatures = [
  'Exam + Cert',
  'Free Digital Textbook',
  'Interactive study guide',
  'Exam Simulator',
  'Flashcard system',
  'First Recert is Free',
  'Job Assurance Guarantee',
  'Unlimited Exam Retakes',
  'SurePass AI',
  'Audio study guide',
  'Performance reporting',
  'Study streaks',
  'Study gamification',
  'Weak point training',
  'Tailored study plans',
  'Magic mnemonics',
  'Exam date tracker',
  '0% interest financing',
];

// Default bonuses list
const defaultBonuses = ['Trainer Stack All-Access', 'CPR/AED Certification', 'Essential cheat sheet'];

// Default bonus configuration
const defaultBonusesConfig: BonusConfig[] = [
  {
    name: 'Trainer Stack All-Access',
    delay: 0,
    durationDays: LIVE_BONUS_ACTIVE_DAYS,
    scarcityMode: 'slots',
    maxUnits: 50,
    finalLowUnitsThreshold: 46,
  },
  { name: 'CPR/AED Certification', delay: 25, hidden: true },
  { name: 'Essential cheat sheet', delay: 0, durationDays: 7, hidden: true },
];

const resolveNumber = (...values: Array<unknown>): number | undefined => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return undefined;
};

const resolveBoolean = (...values: Array<unknown>): boolean | undefined => {
  for (const value of values) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
    }
  }

  return undefined;
};

const resolveString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  return undefined;
};

const normalizeBonusConfig = (bonus: BonusConfig, fallbackName?: string): NormalizedBonusConfig => {
  const delay = resolveNumber(bonus.delay) ?? 0;
  const durationDays = resolveNumber(bonus.durationDays) ?? BONUS_ACTIVE_DAYS;
  const hidden = resolveBoolean(bonus.hidden) ?? false;
  const startDate = resolveString(bonus.startDate);
  const scarcityModeRaw = resolveString(bonus.scarcityMode);
  const scarcityMode: NormalizedBonusConfig['scarcityMode'] =
    scarcityModeRaw === 'slots' || scarcityModeRaw === 'percentage-with-cooldown' || scarcityModeRaw === 'none'
      ? scarcityModeRaw
      : 'default';
  const maxUnits = resolveNumber(bonus.maxUnits);
  const finalLowUnitsThreshold = resolveNumber(bonus.finalLowUnitsThreshold);
  const cooldownLabel = resolveString(bonus.cooldownLabel);

  return {
    name: resolveString(bonus.name, fallbackName) || fallbackName || 'Bonus',
    delay,
    durationDays: normalizePositiveNumber(durationDays, BONUS_ACTIVE_DAYS),
    hidden,
    startDate,
    scarcityMode,
    maxUnits,
    finalLowUnitsThreshold,
    cooldownLabel,
  };
};

const normalizeBonusNames = (value?: Array<string | { name?: unknown }>) => {
  if (!Array.isArray(value)) return defaultBonuses;
  const normalized = value
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && 'name' in item) {
        const name = (item as { name?: unknown }).name;
        return typeof name === 'string' ? name : undefined;
      }
      return undefined;
    })
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0);

  return normalized.length > 0 ? normalized : defaultBonuses;
};

const getInstallmentDisplayPricing = (installmentPrice?: PriceLike | null) => {
  if (!installmentPrice?.amount) {
    return null;
  }

  const totalBeforeDiscounts = installmentPrice.amount;
  const mainDiscount = installmentPrice.defaultCoupon?.discountAmount || 0;
  const totalAfterMainCoupon = Math.max(totalBeforeDiscounts - mainDiscount, 0);
  const monthlyAfterMainCoupon = Math.round(totalAfterMainCoupon / 12);

  const firstMonthCoupon = installmentPrice.firstMonthDiscountCoupon;
  const firstMonthDiscountAmount = firstMonthCoupon?.discountAmount || 0;
  const firstMonthDiscountType = firstMonthCoupon?.discountType;
  const firstMonthDiscount =
    firstMonthDiscountType === 'percentage'
      ? Math.round((monthlyAfterMainCoupon * firstMonthDiscountAmount) / 100)
      : Math.round(firstMonthDiscountAmount);

  const firstMonthStartPrice = Math.max(monthlyAfterMainCoupon - firstMonthDiscount, 0);
  const totalAfterAllCoupons = Math.max(totalAfterMainCoupon - firstMonthDiscount, 0);

  const isEarlyFollowUpPaymentEnabled =
    installmentPrice.earlyFollowUpPaymentEnabled === true || installmentPrice.commitmentPaymentEnabled === true;
  const commitmentPaymentAmountRaw = Number(
    installmentPrice.earlyFollowUpPaymentAmount ?? installmentPrice.commitmentPaymentAmount ?? 0,
  );
  const commitmentPaymentAmount =
    isEarlyFollowUpPaymentEnabled && Number.isFinite(commitmentPaymentAmountRaw) && commitmentPaymentAmountRaw > 0
      ? Math.floor(commitmentPaymentAmountRaw)
      : 0;

  const remainingAfterFirstAndCommitment = Math.max(
    totalAfterAllCoupons - firstMonthStartPrice - commitmentPaymentAmount,
    0,
  );
  const recurringMonthlyAmount = Math.floor(remainingAfterFirstAndCommitment / 11);

  return {
    totalBeforeDiscounts,
    totalAfterMainCoupon,
    monthlyAfterMainCoupon,
    firstMonthDiscount,
    firstMonthStartPrice,
    totalAfterAllCoupons,
    commitmentPaymentAmount,
    recurringMonthlyAmount,
  };
};

// Plan configurations - now dynamic based on API data
const getPlanConfigs = (
  displayAccentColor: string,
  apiData: APIResponse | null,
  prices?: {
    purchase?: any;
    installment?: any;
    monthlySubscriptions?: any;
  },
  customPlanConfigs?: Partial<PlanConfigs>,
  features?: string[],
  bonuses?: string[],
  featureData?: Record<string, FeatureData>,
  isCertificate?: boolean,
  isNewUserForBronze?: boolean,
): PlanConfigs => {
  // Default values if API data is not available
  const defaults = {
    mvpOriginal: 999,
    mvpSale: 699,
    mvpMonthly: 70,
    allstarSale: 699,
    rookieSale: 399,
  };

  let mvpOriginal = defaults.mvpOriginal;
  let mvpSale = defaults.mvpSale;
  let mvpMonthly = defaults.mvpMonthly;
  let allstarSale = defaults.allstarSale;
  let rookieSale = defaults.rookieSale;
  let allstarMonthly: number | undefined;
  let rookieMonthly: number | undefined;

  const purchasePrices = prices?.purchase || apiData?.prices?.purchase;
  const installmentPrices = prices?.installment || apiData?.prices?.installment;
  const monthlySubscriptionPrices = prices?.monthlySubscriptions || apiData?.prices?.monthlySubscriptions;

  if (purchasePrices) {
    // MVP pricing from course prices or API.
    //
    // Every tier is read defensively: a product priced at only some levels serialises the others as
    // an explicit `null` (not undefined), so `purchasePrices.silver.amount` throws rather than
    // yielding undefined. Bundle Builder packages are the first products sold at MVP only, which is
    // what surfaced this — but any gold-only course would have crashed the whole pricing table the
    // same way.
    mvpOriginal = purchasePrices.gold?.amount ?? mvpOriginal;
    mvpSale = purchasePrices.gold?.defaultCoupon
      ? mvpOriginal - purchasePrices.gold.defaultCoupon.discountAmount
      : mvpOriginal;

    if (installmentPrices?.gold?.amount) {
      const mvpInstallmentPricing = getInstallmentDisplayPricing(installmentPrices.gold);
      if (mvpInstallmentPricing) {
        mvpMonthly = mvpInstallmentPricing.recurringMonthlyAmount;
      }
    } else if (monthlySubscriptionPrices?.gold?.amount) {
      const mvpSubscriptionAmount = monthlySubscriptionPrices.gold.amount;
      const mvpSubscriptionDiscount = monthlySubscriptionPrices.gold.defaultCoupon?.discountAmount || 0;
      mvpMonthly = mvpSubscriptionAmount - mvpSubscriptionDiscount;

      const allstarSubscriptionAmount = monthlySubscriptionPrices.silver?.amount ?? 0;
      const allstarSubscriptionDiscount = monthlySubscriptionPrices.silver?.defaultCoupon?.discountAmount || 0;
      allstarMonthly = allstarSubscriptionAmount - allstarSubscriptionDiscount;

      const rookieSubscriptionAmount = monthlySubscriptionPrices.bronze?.amount ?? 0;
      const rookieSubscriptionDiscount = monthlySubscriptionPrices.bronze?.defaultCoupon?.discountAmount || 0;
      rookieMonthly = rookieSubscriptionAmount - rookieSubscriptionDiscount;
    }

    // Other plans
    allstarSale = purchasePrices.silver?.amount ?? allstarSale;
    rookieSale = purchasePrices.bronze?.amount ?? rookieSale;
  }

  /**
   * Which plans this product is actually sold at.
   *
   * A missing tier is not the same as a tier priced at the default. Falling back to the hardcoded
   * $699/$399 would advertise All-Star and Rookie for a product that cannot be bought at either —
   * the server rejects those levels with a 400, so the buttons would fail on click. Omitting the
   * plan removes it from every column, row and selector at once, because they all iterate this
   * object.
   */
  const soldAt = {
    mvp: true,
    allstar: Boolean(purchasePrices?.silver?.amount || monthlySubscriptionPrices?.silver?.amount),
    rookie:
      isCertificate && isNewUserForBronze
        ? false
        : Boolean(purchasePrices?.bronze?.amount || monthlySubscriptionPrices?.bronze?.amount),
  };

  // Products with no price data at all keep every plan: that is the pre-existing default-pricing
  // path used by previews and by courses whose prices have not loaded yet.
  const hasAnyPriceData = Boolean(purchasePrices || monthlySubscriptionPrices);
  const includePlan = (plan: keyof typeof soldAt) => {
    if (isCertificate && isNewUserForBronze && plan === 'rookie') {
      return false;
    }
    return !hasAnyPriceData || soldAt[plan];
  };

  // Generate feature configuration from FeatureData plan flags or use API data if available
  const featuresList = features || defaultFeatures;
  const bonusesList = normalizeBonusNames(bonuses as Array<string | { name?: unknown }> | undefined);

  // Use passed featureData or fallback to defaultFeatureData
  const currentFeatureData = featureData || defaultFeatureData;

  // Create feature arrays based on plan flags in FeatureData
  const mvpFeatures =
    apiData?.planFeatures?.mvp ||
    featuresList.map((featureName) => {
      const featureInfo = currentFeatureData[featureName];
      return featureInfo ? featureInfo.mvp : true;
    });

  const allstarFeatures =
    apiData?.planFeatures?.allstar ||
    featuresList.map((featureName) => {
      const featureInfo = currentFeatureData[featureName];
      return featureInfo ? featureInfo.allstar : true;
    });

  const rookieFeatures =
    apiData?.planFeatures?.rookie ||
    featuresList.map((featureName) => {
      const featureInfo = currentFeatureData[featureName];
      return featureInfo ? featureInfo.rookie : true;
    });

  // Generate bonus configuration from FeatureData plan flags or use API data if available
  const mvpBonuses = bonusesList.map((bonusName, index) => {
    if (typeof apiData?.planBonuses?.mvp?.[index] === 'boolean') {
      return apiData.planBonuses.mvp[index];
    }
    const bonusInfo = currentFeatureData[bonusName];
    return bonusInfo ? bonusInfo.mvp : true;
  });

  const allstarBonuses = bonusesList.map((bonusName, index) => {
    if (typeof apiData?.planBonuses?.allstar?.[index] === 'boolean') {
      return apiData.planBonuses.allstar[index];
    }
    const bonusInfo = currentFeatureData[bonusName];
    return bonusInfo ? bonusInfo.allstar : false;
  });

  const rookieBonuses = bonusesList.map((bonusName, index) => {
    if (typeof apiData?.planBonuses?.rookie?.[index] === 'boolean') {
      return apiData.planBonuses.rookie[index];
    }
    const bonusInfo = currentFeatureData[bonusName];
    return bonusInfo ? bonusInfo.rookie : false;
  });

  // Default plan configurations
  const defaultPlans: PlanConfigs = {
    mvp: {
      name: 'MVP',
      lottie: 'mvp',
      color: `border-4 border-[${displayAccentColor}]`,
      bgColor: `bg-[${displayAccentColor}]`,
      features: mvpFeatures,
      bonuses: mvpBonuses,
      pricing: {
        original: mvpOriginal,
        sale: mvpSale,
        monthly: mvpMonthly,
        installmentTotal: getInstallmentDisplayPricing(installmentPrices?.gold)?.totalAfterAllCoupons,
      },
      planId: 'ml',
      badge: '99% PASS RATE',
    },
    allstar: {
      name: isCertificate ? 'Rookie' : 'All-Star',
      lottie: 'allstar',
      color: 'border border-gray-200',
      bgColor: 'bg-gray-600',
      features: allstarFeatures,
      bonuses: allstarBonuses,
      pricing: {
        sale: allstarSale,
        monthly: allstarMonthly,
      },
      planId: 'al',
    },
    rookie: {
      name: 'Rookie',
      lottie: 'rookie',
      color: 'border border-gray-200',
      bgColor: 'bg-gray-400',
      features: rookieFeatures,
      bonuses: rookieBonuses,
      pricing: {
        sale: rookieSale,
        monthly: rookieMonthly,
      },
      planId: 'rl',
    },
  };

  // Merge with custom configurations if provided
  const merged = customPlanConfigs
    ? {
        mvp: { ...defaultPlans.mvp, ...customPlanConfigs.mvp },
        allstar: { ...defaultPlans.allstar, ...customPlanConfigs.allstar },
        rookie: { ...defaultPlans.rookie, ...customPlanConfigs.rookie },
      }
    : defaultPlans;

  // Drop the plans this product is not sold at. Key order is preserved for the ones that remain, so
  // the column order does not change for products priced at all three levels.
  return Object.fromEntries(
    Object.entries(merged).filter(([plan]) => includePlan(plan as keyof typeof soldAt)),
  ) as PlanConfigs;
};

export default function PricingTable({
  accentColor,
  courseId = '2b91e4ec-aed2-4542-b0ea-40d49d31bed5',
  directionId,
  prices,
  onPurchase,
  customLogoUrl,
  featuresData,
  lottieAnimations,
  features,
  bonuses,
  bonusesConfig,
  planConfigs: customPlanConfigs,
  headerConfig,
  courseName,
  bundleCourseNames,
  pricingType,
  isCertificate,
  hasBronzeAccess,
}: PricingTableProps) {
  // Use provided data or fall back to defaults
  const featureData = featuresData || defaultFeatureData;
  const baseLottieAnimations = getDefaultLottieAnimations();
  const resolvedOverrides = Object.fromEntries(
    Object.entries(lottieAnimations || {}).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  ) as Partial<LottieAnimations>;
  const lottieAnimationUrls = { ...baseLottieAnimations, ...resolvedOverrides } as LottieAnimations;
  const featuresList = features || defaultFeatures;
  const bonusesList = useMemo(
    () => normalizeBonusNames(bonuses as Array<string | { name?: unknown }> | undefined),
    [bonuses],
  );
  const bonusConfigSource = bonusesConfig || defaultBonusesConfig;
  const bonusConfigList = useMemo(
    () =>
      bonusConfigSource.map((bonus, index) =>
        normalizeBonusConfig(bonus, bonusesList[index] || bonus?.name || `Bonus ${index + 1}`),
      ),
    [bonusConfigSource, bonusesList],
  );
  const getBonusConfigByName = useCallback(
    (bonusName: string): NormalizedBonusConfig => {
      const byName = bonusConfigList.find((bonus) => bonus.name === bonusName);
      if (byName) return byName;

      const bonusIndex = bonusesList.findIndex((item) => item === bonusName);
      if (bonusIndex >= 0 && bonusConfigList[bonusIndex]) {
        return bonusConfigList[bonusIndex];
      }

      return normalizeBonusConfig({ name: bonusName }, bonusName);
    },
    [bonusConfigList, bonusesList],
  );
  const visibleBonusesList = useMemo(
    () => bonusesList.filter((bonusName) => !getBonusConfigByName(bonusName).hidden),
    [bonusesList, getBonusConfigByName],
  );
  // Use default color for display if not provided
  const displayAccentColor = accentColor || '#FF6B35';

  // Default header configuration
  const defaultHeaderConfig: HeaderConfig = {
    title: courseName ? `Choose your ${courseName} plan` : 'COMPARE PLANS AND FIND YOURS',
    subtitle: '12 payments (0% interest) – Or save 17% with one-time payment',
  };

  const headerContent = headerConfig || defaultHeaderConfig;

  const formatCourseList = (names: string[]) => {
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  };

  const bundleCoursesText =
    bundleCourseNames && bundleCourseNames.length > 0 ? formatCourseList(bundleCourseNames) : '';
  const showBundleCoursesNote = Boolean(bundleCoursesText);

  // Default-select one-time payment
  const [paymentType, setPaymentType] = useState<'onetime' | 'plan'>('onetime');
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<keyof PlanConfigs>('mvp');
  const [isMounted, setIsMounted] = useState(false);
  const [apiData, setApiData] = useState<APIResponse | null>(null);
  const [bonusDate, setBonusDate] = useState('');
  const [bonusClaimedData, setBonusClaimedData] = useState<Record<string, BonusClaimedData>>({});
  const [isTabsSticky, setIsTabsSticky] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isCertificationCourse = isCertificate ?? pricingType === 'certification';
  const isNewUserForBronze = !hasBronzeAccess;

  const planConfigs = getPlanConfigs(
    displayAccentColor,
    apiData,
    prices,
    customPlanConfigs,
    featuresList,
    visibleBonusesList,
    featureData,
    isCertificationCourse,
    isNewUserForBronze,
  );

  const gridColumnsStyle = {
    gridTemplateColumns: `1.6fr ${'1fr '.repeat(Object.keys(planConfigs).length).trim()}`,
  };

  const hasInstallmentPrices = Boolean(prices?.installment) || Boolean(apiData?.prices?.installment);
  const hasMonthlySubscriptionPrices =
    Boolean(prices?.monthlySubscriptions) || Boolean(apiData?.prices?.monthlySubscriptions);

  const isStudyMaterial =
    pricingType === 'study-material'
      ? true
      : pricingType === 'certification'
      ? false
      : !hasInstallmentPrices && hasMonthlySubscriptionPrices;

  const oneTimeLabel = isStudyMaterial ? 'Lifetime' : 'One-time';
  const oneTimePaymentLabel = isStudyMaterial ? 'Lifetime access' : 'One-time payment';

  useEffect(() => {
    if (isStudyMaterial && selectedPlan !== 'mvp' && paymentType !== 'plan') {
      setPaymentType('plan');
    }
  }, [isStudyMaterial, paymentType, selectedPlan]);

  const selectedPaymentType = isStudyMaterial && selectedPlan !== 'mvp' ? 'plan' : paymentType;

  void directionId;
  void customLogoUrl;

  // Utility functions
  const getToday = () => {
    const dateObj = new Date();
    const day = dateObj.getDate();
    const month = dateObj.toLocaleString('default', { month: 'long' });
    const nthNumber = (number: number) => {
      if (number > 3 && number < 21) return 'th';
      switch (number % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };
    return `${month} ${day}${nthNumber(day)}`;
  };

  const calcPercentage = (x: number, y: number) => {
    return Math.round((x / y) * 100);
  };

  // Generate accent color-based gradient with better contrast (closer to accent color)
  const getAccentGradient = (): React.CSSProperties => {
    // Convert hex to RGB
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;
    };

    const rgb = hexToRgb(displayAccentColor);
    if (!rgb)
      return {
        background: 'linear-gradient(to right, rgba(28, 191, 115, 0.15), rgba(28, 191, 115, 0.05))',
      }; // fallback

    // Create gradient with more contrast - first color closer to accent, second very light
    const fromColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`; // More opaque (closer to accent color)
    const toColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.05)`; // Very light

    return {
      background: `linear-gradient(to right, ${fromColor}, ${toColor})`,
    };
  };

  // Generate accent color-based background with better contrast
  const getAccentBackground = (): {
    backgroundColor: string;
    borderColor: string;
  } => {
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;
    };

    const rgb = hexToRgb(displayAccentColor);
    if (!rgb)
      return {
        backgroundColor: 'rgba(28, 191, 115, 0.08)',
        borderColor: 'rgba(28, 191, 115, 0.2)',
      }; // fallback

    // Background lighter, border more visible
    const backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08)`; // Slightly more opaque background
    const borderColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2)`; // More visible border

    return { backgroundColor, borderColor };
  };

  // Generate dynamic shadow style for MVP cells based on accent color
  const getMvpShadowStyle = (): React.CSSProperties => {
    // Convert hex to RGB for shadow
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;
    };

    const rgb = hexToRgb(displayAccentColor);
    if (!rgb) return { boxShadow: '0px 10px 40px rgba(28, 191, 115, 0.15)' }; // fallback

    return {
      position: 'relative' as const,
      overflow: 'visible' as const,
      zIndex: 5,
      boxShadow: `0px 10px 40px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
    };
  };

  const getDynamicText = () => {
    const purchasePrices = prices?.purchase || apiData?.prices?.purchase;
    const installmentPrices = prices?.installment || apiData?.prices?.installment;
    const monthlySubscriptionPrices = prices?.monthlySubscriptions || apiData?.prices?.monthlySubscriptions;

    if (!purchasePrices || (!installmentPrices && !monthlySubscriptionPrices))
      return {
        flashSaleText: 'Flash Sale: Save 30%',
        installmentText: '$70 Down, 0% Interest',
        savingsText: '<strong>Save $300</strong> With Our One-Time Payment Discount',
      };

    const mvpLifetime = purchasePrices.gold.amount;
    const mvpLifetimeDiscount = purchasePrices.gold.defaultCoupon?.discountAmount || 0;

    const lifetimeFinalPrice = mvpLifetime - mvpLifetimeDiscount;

    if (isStudyMaterial && monthlySubscriptionPrices?.gold?.amount) {
      const mvpMonthly = monthlySubscriptionPrices.gold.amount;
      const mvpMonthlyDiscount = monthlySubscriptionPrices.gold.defaultCoupon?.discountAmount || 0;
      const monthlyFinalPrice = mvpMonthly - mvpMonthlyDiscount;

      const discountPercentage =
        paymentType === 'onetime'
          ? mvpLifetimeDiscount
            ? calcPercentage(mvpLifetimeDiscount, mvpLifetime)
            : 30
          : mvpMonthlyDiscount
          ? calcPercentage(mvpMonthlyDiscount, mvpMonthly)
          : 30;

      return {
        flashSaleText: `Flash Sale: Save ${discountPercentage}%`,
        installmentText: `$${monthlyFinalPrice} / month`,
        savingsText: '<strong>Pay once</strong> and keep access forever',
      };
    }

    if (!installmentPrices?.gold?.amount) {
      return {
        flashSaleText: 'Flash Sale: Save 30%',
        installmentText: '$70 Down, 0% Interest',
        savingsText: '<strong>Save $300</strong> With Our One-Time Payment Discount',
      };
    }

    const mvpInstallmentPricing = getInstallmentDisplayPricing(installmentPrices.gold);
    if (!mvpInstallmentPricing) {
      return {
        flashSaleText: 'Flash Sale: Save 30%',
        installmentText: '$70 Down, 0% Interest',
        savingsText: '<strong>Save $300</strong> With Our One-Time Payment Discount',
      };
    }

    const installmentTotalBeforeFirstMonthDiscount = mvpInstallmentPricing.totalAfterAllCoupons;
    const firstMonthPrice = mvpInstallmentPricing.firstMonthStartPrice;

    const savingsAmountRaw = installmentTotalBeforeFirstMonthDiscount - lifetimeFinalPrice;
    const savingsAmount = Math.max(0, savingsAmountRaw);

    const discountPercentage = mvpLifetimeDiscount ? calcPercentage(mvpLifetimeDiscount, mvpLifetime) : 30;

    return {
      flashSaleText: mvpLifetimeDiscount ? `Flash Sale: Save ${discountPercentage}%` : 'Flash Sale: Save 30%',
      installmentText: `$${firstMonthPrice} Down, 0% Interest`,
      savingsText: `<strong>Save $${savingsAmount}</strong> compared to the payment <br>plan`,
    };
  };

  // Fetch API data (only when prices aren't passed from the app)
  // Expected JSON structure for dynamic plan features:
  // {
  //   "prices": { ... existing pricing structure ... },
  //   "planFeatures": {
  //     "mvp": [true, true, true, true, true, true, true, true, true, true],
  //     "allstar": [true, true, true, true, true, true, true, false, false, false],
  //     "rookie": [true, true, true, false, false, false, false, false, false, false]
  //   },
  //   "planBonuses": {
  //     "mvp": [true, true, true, true, true],
  //     "allstar": [false, false, false, false, false],
  //     "rookie": [false, false, false, false, false]
  //   }
  // }
  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        const apiURL = window.location.origin.includes('vercel.app') ? 'staging-api' : 'api';
        const response = await fetch(`https://${apiURL}.traineracademy.org/courses/${courseId}/getById`);
        const data = await response.json();
        setApiData(data);
      } catch (error) {
        console.error('Failed to fetch pricing data:', error);
      }
    };

    if (!prices?.purchase || (!prices?.installment && !prices?.monthlySubscriptions)) {
      fetchPricingData();
    }
  }, [courseId]);

  // Calculate bonus data
  useEffect(() => {
    const updateBonusData = () => {
      setBonusDate(getToday());

      const currentDate = new Date();
      const bonusData: Record<string, BonusClaimedData> = {};
      const bonusConfigMap = new Map(bonusConfigList.map((bonus) => [bonus.name, bonus]));

      visibleBonusesList.forEach((bonusName) => {
        const bonus =
          bonusConfigMap.get(bonusName) ||
          normalizeBonusConfig(
            {
              name: bonusName,
            },
            bonusName,
          );

        const startDate = bonus.startDate ? new Date(bonus.startDate) : LIVE_BONUS_ANCHOR_UTC;
        const delayHours = Number.isFinite(bonus.delay) ? bonus.delay : 0;
        const scarcityMode = bonus.scarcityMode || 'default';

        if (scarcityMode === 'slots') {
          const maxUnits = bonus.maxUnits ?? SLOT_BADGE_DEFAULT_MAX_UNITS;
          const maxClaimed = Math.min(SLOT_BADGE_DEFAULT_MAX_CLAIMED, Math.max(maxUnits - 1, 1));
          const lowUnitsThreshold = bonus.finalLowUnitsThreshold ?? Math.max(maxUnits - 4, 1);

          const elapsedHours = getCycleElapsedHours(startDate, currentDate, delayHours, SLOT_BADGE_DEFAULT_ACTIVE_DAYS);
          const claimedUnits = calculateSegmentedCount(elapsedHours, SLOT_BADGE_CURVE, maxClaimed);

          if (claimedUnits >= lowUnitsThreshold) {
            bonusData[bonusName] = {
              text: `Only ${maxUnits - claimedUnits} left`,
              color: 'red',
            };
            return;
          }

          bonusData[bonusName] = {
            text: `${claimedUnits} / ${maxUnits} claimed`,
            color: getSlotBadgeColor(claimedUnits),
          };
          return;
        }

        if (scarcityMode === 'percentage-with-cooldown') {
          const elapsedHours = getCycleElapsedHours(
            startDate,
            currentDate,
            delayHours,
            PERCENT_COOLDOWN_CYCLE_SPAN_DAYS,
          );

          if (elapsedHours > PERCENT_COOLDOWN_ACTIVE_HOURS) {
            bonusData[bonusName] = {
              text: bonus.cooldownLabel || PERCENT_COOLDOWN_DEFAULT_LABEL,
              color: 'yellow',
              isMessage: true,
            };
            return;
          }

          const percentage = calculateSegmentedPercentage(
            Math.min(elapsedHours, PERCENT_COOLDOWN_ACTIVE_HOURS),
            PERCENT_COOLDOWN_CURVE,
            97,
          );
          bonusData[bonusName] = {
            text: `${percentage}% claimed`,
            color: getLiveBonusColor(percentage),
          };
          return;
        }

        // Explicitly render the bonus row without a scarcity badge.
        if (scarcityMode === 'none') {
          return;
        }

        const percentage = calculateBonusPercentage(startDate, currentDate, bonus);
        if (percentage !== null) {
          bonusData[bonusName] = {
            text: `${percentage}% claimed`,
            color: usesLiveBadge(bonus) ? getLiveBonusColor(percentage) : getBonusClaimedColor(percentage),
          };
        }
      });

      setBonusClaimedData(bonusData);
    };

    updateBonusData();
    const id = setInterval(updateBonusData, 60000);
    return () => clearInterval(id);
  }, [bonusConfigList, visibleBonusesList]);

  const dynamicText = getDynamicText();
  const purchasePrices = prices?.purchase || apiData?.prices?.purchase;
  const installmentPrices = prices?.installment || apiData?.prices?.installment;
  const monthlySubscriptionPrices = prices?.monthlySubscriptions || apiData?.prices?.monthlySubscriptions;
  const mvpInstallmentPricing = getInstallmentDisplayPricing(installmentPrices?.gold);

  const getStartForPrice = () => {
    return mvpInstallmentPricing?.firstMonthStartPrice ?? 70;
  };

  // Anti-flicker sticky tabs with hysteresis
  useEffect(() => {
    let ticking = false;
    let lastKnownShouldBeSticky = false;
    let initialTop = 0;
    let hasInitialized = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const pricingSection = document.querySelector('[data-section="pricing"]');
          if (!pricingSection) {
            ticking = false;
            return;
          }

          const mobileLayout = pricingSection.querySelector('.lg\\:hidden');
          if (!mobileLayout) {
            ticking = false;
            return;
          }

          // Initialize the tabs container reference on first run
          if (!hasInitialized) {
            const tabsContainer = mobileLayout.querySelector('div[class*="transition-all"]');
            if (tabsContainer) {
              const tabsContainerRect = tabsContainer.getBoundingClientRect();
              initialTop = tabsContainerRect.top + window.scrollY;
              hasInitialized = true;
            }
          }

          if (!hasInitialized) {
            ticking = false;
            return;
          }

          const stickyNavHeight = 64;
          const stickyThreshold = stickyNavHeight + 16;
          const currentScrollY = window.scrollY;

          // Use absolute positioning calculation instead of getBoundingClientRect
          // to avoid layout feedback loops
          const tabsAbsoluteTop = initialTop - currentScrollY;
          const mobileLayoutRect = mobileLayout.getBoundingClientRect();
          const mobileLayoutBottom = mobileLayoutRect.bottom;

          // Add hysteresis (different thresholds for activating vs deactivating)
          const hysteresisOffset = 5; // 5px buffer to prevent flickering

          let shouldBeSticky = false;

          if (!lastKnownShouldBeSticky) {
            // Not currently sticky - need to cross activation threshold
            shouldBeSticky =
              tabsAbsoluteTop <= stickyThreshold - hysteresisOffset && mobileLayoutBottom > stickyThreshold + 120;
          } else {
            // Currently sticky - need to cross deactivation threshold
            shouldBeSticky =
              tabsAbsoluteTop <= stickyThreshold + hysteresisOffset && mobileLayoutBottom > stickyThreshold + 120;
          }

          // Only change state when crossing hysteresis boundaries
          if (shouldBeSticky !== lastKnownShouldBeSticky) {
            lastKnownShouldBeSticky = shouldBeSticky;

            // Clear any pending debounce
            if (debounceTimeoutRef.current) {
              clearTimeout(debounceTimeoutRef.current);
            }

            // Small debounce to prevent rapid flickering at exact boundaries
            debounceTimeoutRef.current = setTimeout(() => {
              setIsTabsSticky(shouldBeSticky);
            }, 10); // Very short debounce, just enough to prevent rapid toggles
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    // Reset initialization on mount
    hasInitialized = false;

    const handleResize = () => {
      hasInitialized = false;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);

      // Clean up debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleFeatureClick = (feature: string) => {
    setSelectedFeature(feature);
  };

  const handlePurchase = (planId: string, paymentOverride?: 'onetime' | 'plan') => {
    if (onPurchase) {
      const resolvedPaymentType = paymentOverride || selectedPaymentType;
      onPurchase(planId, resolvedPaymentType);
    }
  };

  if (!isMounted) {
    return (
      <div className="py-12 sm:py-16 lg:py-20 bg-gray-50 flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-gray-200 border-t-gray-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className={`pricing-table-root ${montserrat.className}`}>
      <style>{`
        .table-cell-shadow {
          position: relative;
          z-index: 0;
        }
        .table-cell-shadow:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          box-shadow: 0px 10px 40px #0000001a;
          z-index: -1;
          pointer-events: none;
        }
        .table-cell-shadow > * {
          position: relative;
          z-index: 1;
        }
        .table-cell-shadow-lg {
          position: relative;
          z-index: 0;
        }
        .table-cell-shadow-lg:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          box-shadow: 0px 8px 25px rgba(0, 0, 0, 0.15);
          z-index: -1;
          pointer-events: none;
        }
        .table-cell-shadow-lg > * {
          position: relative;
          z-index: 1;
        }
        .table-cell-shadow-2xl {
          position: relative;
          z-index: 0;
          overflow: visible;
        }
        .table-cell-shadow-2xl:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          box-shadow: 0px 10px 40px #0000001a;
          z-index: -1;
          pointer-events: none;
        }
        .table-cell-shadow-2xl > * {
          position: relative;
          z-index: 1;
        }
        .table-cell-shadow-mvp {
          position: relative;
          overflow: visible;
        }
        .table-cell-shadow-mvp-green {
          position: relative;
          overflow: visible;
          z-index: 5;
        }
        .border-radius-top:before {
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
        }
        .border-radius-bottom:before {
          border-bottom-left-radius: 16px;
          border-bottom-right-radius: 16px;
        }
        @keyframes subtle-shake {
          0%,
          100% {
            transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70%,
          90% {
            transform: translateX(-1px);
          }
          20%,
          40%,
          60%,
          80% {
            transform: translateX(1px);
          }
        }
        .flash-sale-wiggle {
          animation: subtle-shake 5s ease-in-out infinite;
        }
        .flash-sale-wiggle:hover {
          animation-play-state: paused;
        }
        @keyframes live-bonus-tick {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.06);
          }
          100% {
            transform: scale(1);
          }
        }
        .live-bonus-tick {
          animation: live-bonus-tick 0.4s ease-in-out;
        }
      `}</style>
      <section data-section="pricing" className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h2
              className={`text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 mb-4 ${montserrat.className}`}
              dangerouslySetInnerHTML={{
                __html: headerContent.title
                  .replace(/className='text-\[([^\]]+)\]'/g, 'style="color: $1"')
                  .replace(/AND FIND YOURS|FIND YOURS/, `<span style="color: ${displayAccentColor}">$&</span>`),
              }}
            />
          </div>

          {/* Desktop Pricing Table */}
          <div className="hidden lg:block max-w-7xl mx-auto">
            {/* Table Container */}
            <div className="space-y-0">
              {/* Header Row - Plan Names and Badge */}
              <div className="grid gap-6" style={gridColumnsStyle}>
                {/* Features Column Header */}
                <div className="bg-white rounded-t-2xl border border-gray-200 border-radius-top p-6 border-b-0">
                  <div className="mb-0 h-36 flex items-center">
                    <div className="flex-shrink-0 mr-4">
                      <LottiePlayer src="thumb" width={60} height={60} lottieAnimations={lottieAnimationUrls} />
                    </div>
                    <div className="flex-1">
                      {(headerContent.showBogoFeatureNote || showBundleCoursesNote) && (
                        <div className="text-xs text-gray-600 mb-2 font-medium leading-tight">
                          The following study features apply to both{' '}
                          {showBundleCoursesNote ? bundleCoursesText : 'the IPTA CPT and Nutrition Academy CNS'}
                          {showBundleCoursesNote ? '' : ' certifications'}
                        </div>
                      )}
                      <div
                        style={{
                          color: displayAccentColor,
                          textDecoration: 'underline dashed',
                          textDecorationColor: displayAccentColor,
                          textDecorationThickness: '1px',
                          textUnderlineOffset: '4px',
                          WebkitTextDecorationLine: 'underline',
                          WebkitTextDecorationColor: displayAccentColor,
                          WebkitTextDecorationStyle: 'dashed',
                        }}
                        className="text-l font-bold leading-tight cursor-pointer max-w-xs">
                        Click any feature below to
                        <br /> learn more about it!
                      </div>
                    </div>
                  </div>
                </div>

                {/* Plan Headers */}
                {Object.entries(planConfigs).map(([key, plan]) => (
                  <div
                    key={key}
                    className={`relative bg-white rounded-t-2xl border-radius-top ${
                      key === 'mvp' ? 'border-4 border-b-0' : 'border-l border-r border-t border-gray-200'
                    } p-6`}
                    style={{
                      borderColor: key === 'mvp' ? displayAccentColor : undefined,
                      ...(key === 'mvp' ? getMvpShadowStyle() : {}),
                    }}>
                    {/* Badge for MVP */}
                    {plan.badge && (
                      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                        <div
                          className="text-white px-6 py-2 rounded-full font-bold text-sm whitespace-nowrap flex items-center justify-center"
                          style={{ backgroundColor: displayAccentColor }}>
                          {plan.badge}
                        </div>
                      </div>
                    )}

                    <div className="text-center pt-2">
                      <div className="h-36 flex flex-col justify-center items-center">
                        <div className="flex justify-center mb-3">
                          <LottiePlayer
                            src={plan.lottie}
                            width={100}
                            height={100}
                            lottieAnimations={lottieAnimationUrls}
                          />
                        </div>
                        <h3 className={`text-2xl font-black text-gray-900 ${montserrat.className}`}>{plan.name}</h3>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Feature Rows */}
              {featuresList.map((feature, index) => (
                <div key={index} className="grid gap-6" style={gridColumnsStyle}>
                  {/* Feature Name */}
                  <div className="bg-white border-l border-r border-gray-200 px-6 py-3 h-12 flex items-center">
                    <span className="text-lg font-bold text-gray-700 mr-2">+</span>
                    <span
                      className="text-gray-900 text-base font-bold cursor-pointer transition-colors hover:opacity-80"
                      style={{
                        textDecoration: 'underline dashed',
                        textDecorationThickness: '1px',
                        textUnderlineOffset: '7px',
                        textDecorationSkipInk: 'none',
                        textDecorationSkip: 'none',
                        color: '#333',
                        display: 'inline-block',
                        lineHeight: '1.4',
                      }}
                      onClick={() => handleFeatureClick(feature)}>
                      {feature}
                    </span>
                  </div>

                  {/* Feature Checkmarks */}
                  {Object.entries(planConfigs).map(([key, plan]) => (
                    <div
                      key={key}
                      className={`bg-white ${
                        key === 'mvp' ? 'border-l-4 border-r-4' : 'border-l border-r border-gray-200'
                      } px-6 py-3 h-12 flex justify-center items-center`}
                      style={{
                        borderColor: key === 'mvp' ? displayAccentColor : undefined,
                        ...(key === 'mvp' ? getMvpShadowStyle() : {}),
                      }}>
                      {plan.features[index] ? (
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: displayAccentColor }}>
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {/* Pricing Row */}
              <div className="grid gap-6" data-section="pricing-row" style={gridColumnsStyle}>
                {/* Pricing Label */}
                <div
                  className="bg-white border-l border-r border-gray-200 border-t-0 px-6 py-4 h-20 flex items-center"
                  style={{ paddingTop: '48px', paddingBottom: '48px' }}>
                  <div style={{ color: displayAccentColor }} className={`text-lg font-bold ${montserrat.className}`}>
                    Pricing
                  </div>
                </div>

                {/* Pricing for each plan */}
                {Object.entries(planConfigs).map(([key, plan]) => (
                  <div
                    key={key}
                    className={`bg-white border-t-0 ${
                      key === 'mvp' ? 'border-l-4 border-r-4' : 'border-l border-r border-gray-200'
                    } px-3 py-4 h-20 flex items-center justify-center`}
                    style={{
                      borderColor: key === 'mvp' ? displayAccentColor : undefined,
                      paddingTop: '48px',
                      paddingBottom: '48px',
                      ...(key === 'mvp' ? getMvpShadowStyle() : {}),
                    }}>
                    <div className="text-center">
                      {isStudyMaterial ? (
                        key !== 'mvp' ? (
                          <>
                            <div className="h-6 flex items-center justify-center">
                              <div></div>
                            </div>
                            <div className={`text-xl font-black text-gray-900 ${montserrat.className}`}>
                              ${plan.pricing.monthly}
                            </div>
                            <div className="text-gray-800 text-xs font-semibold">per month</div>
                          </>
                        ) : paymentType === 'onetime' ? (
                          <>
                            {plan.pricing.original && (
                              <div className="h-6 flex items-center justify-center">
                                <div className="text-gray-400 line-through text-lg">${plan.pricing.original}</div>
                              </div>
                            )}
                            {!plan.pricing.original && <div className="h-6"></div>}
                            <div className={`text-2xl font-black text-gray-900 ${montserrat.className}`}>
                              ${plan.pricing.sale}
                            </div>
                            <div className="text-gray-800 text-xs font-semibold">{oneTimeLabel}</div>
                          </>
                        ) : plan.pricing.monthly ? (
                          <>
                            <div className="h-6 flex items-center justify-center">
                              {key === 'mvp' && monthlySubscriptionPrices?.gold?.defaultCoupon ? (
                                <div className="text-gray-400 line-through text-lg">
                                  ${monthlySubscriptionPrices.gold.amount}
                                </div>
                              ) : (
                                <div></div>
                              )}
                            </div>
                            <div className={`text-2xl font-black text-gray-900 ${montserrat.className}`}>
                              ${plan.pricing.monthly}
                            </div>
                            <div className="text-gray-800 text-xs font-semibold">per month</div>
                          </>
                        ) : (
                          <>
                            <div className="h-6"></div>
                            <div className="text-xl font-black text-gray-400">N/A</div>
                            <div className="h-4"></div>
                          </>
                        )
                      ) : paymentType === 'onetime' ? (
                        <>
                          {plan.pricing.original && (
                            <div className="h-6 flex items-center justify-center">
                              <div className="text-gray-400 line-through text-lg">${plan.pricing.original}</div>
                            </div>
                          )}
                          {!plan.pricing.original && <div className="h-6"></div>}
                          <div className={`text-2xl font-black text-gray-900 ${montserrat.className}`}>
                            ${plan.pricing.sale}
                          </div>
                          <div className="text-gray-800 text-xs font-semibold">One-time</div>
                        </>
                      ) : plan.pricing.monthly ? (
                        <>
                          <div className="h-6 flex items-center justify-center">
                            {key === 'mvp' && installmentPrices?.gold?.amount ? (
                              <div className="flex items-end w-full gap-4">
                                <div className="flex-1 text-center">
                                  <div className="text-gray-400 line-through text-lg">
                                    ${Math.round(installmentPrices.gold.amount / 12)}
                                  </div>
                                </div>
                                <div className="flex-1 text-center">
                                  <div className="text-gray-400 line-through text-lg">
                                    ${installmentPrices.gold.amount}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div></div>
                            )}
                          </div>
                          <div className="flex items-end w-full mb-2 gap-4">
                            <div className="flex-1 text-center">
                              <div className={`text-2xl font-black text-gray-900 ${montserrat.className}`}>
                                ${plan.pricing.monthly}
                              </div>
                              <div className="text-xs text-gray-800 font-semibold">monthly</div>
                            </div>
                            <div className="flex-1 text-center">
                              <div className={`text-2xl font-black text-gray-900 ${montserrat.className}`}>
                                $
                                {key === 'mvp' && mvpInstallmentPricing
                                  ? mvpInstallmentPricing?.totalAfterAllCoupons
                                  : plan.pricing.installmentTotal}
                              </div>
                              <div className="text-xs text-gray-800 font-semibold whitespace-nowrap">12 months</div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="h-6"></div>
                          <div className="text-xl font-black text-gray-400">N/A</div>
                          <div className="h-4"></div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Bonuses Header Row */}
              <div className="grid gap-6" style={gridColumnsStyle}>
                {/* Bonuses Label */}
                <div
                  className="bg-gray-100 border-l border-r border-gray-200 px-6 py-4 h-20 flex items-center justify-center"
                  style={{ paddingTop: '32px', paddingBottom: '32px' }}>
                  <div className="text-center flex items-center justify-center">
                    <div className="flex justify-center mr-3">
                      <LottiePlayer src="gift" width={50} height={50} lottieAnimations={lottieAnimationUrls} />
                    </div>
                    <span className="text-gray-800 font-bold text-lg">Bonuses for {bonusDate}</span>
                  </div>
                </div>

                {/* Empty cells for plans */}
                {Object.entries(planConfigs).map(([key, _plan]) => (
                  <div
                    key={key}
                    className={`bg-gray-100 ${
                      key === 'mvp' ? 'border-l-4 border-r-4' : 'border-l border-r border-gray-200'
                    } px-6 py-4 h-20`}
                    style={{
                      borderColor: key === 'mvp' ? displayAccentColor : undefined,
                      paddingTop: '32px',
                      paddingBottom: '32px',
                      ...(key === 'mvp' ? getMvpShadowStyle() : {}),
                    }}></div>
                ))}
              </div>

              {/* Spacing Row before Bonuses */}
              <div className="grid gap-6" style={gridColumnsStyle}>
                {/* Spacing cell */}
                <div className="bg-white border-l border-r border-gray-200 h-4"></div>
                {/* Spacing cells for plans */}
                {Object.entries(planConfigs).map(([key, _plan]) => (
                  <div
                    key={key}
                    className={`bg-white ${
                      key === 'mvp' ? 'border-l-4 border-r-4' : 'border-l border-r border-gray-200'
                    } h-4`}
                    style={{
                      borderColor: key === 'mvp' ? displayAccentColor : undefined,
                      ...(key === 'mvp' ? getMvpShadowStyle() : {}),
                    }}></div>
                ))}
              </div>

              {/* Bonus Feature Rows */}
              {visibleBonusesList.map((bonus, index) => (
                <div key={index} className="grid gap-6" style={gridColumnsStyle}>
                  {/* Bonus Name */}
                  <div className={'bg-white border-l border-r border-gray-200 px-6 py-3 h-16 flex items-start pt-4'}>
                    <span className="text-lg font-bold text-gray-700 mr-2">+</span>
                    <span
                      className="text-gray-900 text-base font-bold cursor-pointer transition-colors hover:opacity-80 flex-1"
                      style={{
                        textDecoration: 'underline dashed',
                        textDecorationThickness: '1px',
                        textUnderlineOffset: '7px',
                        textDecorationSkipInk: 'none',
                        textDecorationSkip: 'none',
                        color: '#333',
                        display: 'inline-block',
                        lineHeight: '1.4',
                      }}
                      onClick={() => handleFeatureClick(bonus)}>
                      {bonus}
                    </span>
                    {bonusClaimedData[bonus] &&
                      (bonusClaimedData[bonus].isMessage ? (
                        <span className="text-gray-600 px-3 py-1 rounded text-xs font-semibold ml-2 border border-gray-300 bg-gray-100">
                          {bonusClaimedData[bonus].text}
                        </span>
                      ) : (
                        <span
                          className={`text-white px-3 py-2 rounded text-sm font-bold ml-2 h-6 flex items-center justify-center ${
                            bonusClaimedData[bonus].color === 'green'
                              ? 'bg-green-500'
                              : bonusClaimedData[bonus].color === 'yellow'
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}>
                          {bonusClaimedData[bonus].text}
                        </span>
                      ))}
                  </div>

                  {/* Bonus Checkmarks */}
                  {Object.entries(planConfigs).map(([key, plan]) => (
                    <div
                      key={key}
                      className={`bg-white ${
                        key === 'mvp' ? 'border-l-4 border-r-4' : 'border-l border-r border-gray-200'
                      } px-6 py-3 h-16 flex justify-center items-center`}
                      style={{
                        borderColor: key === 'mvp' ? displayAccentColor : undefined,
                        ...(key === 'mvp' ? getMvpShadowStyle() : {}),
                      }}>
                      {plan.bonuses[index] ? (
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: displayAccentColor }}>
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {/* Purchase Row */}
              <div className="purchase-row">
                <div className="grid gap-6" style={gridColumnsStyle}>
                  {/* Payment Selection */}
                  <div className="bg-white rounded-b-2xl border-l border-r border-b border-gray-200 border-radius-bottom p-6">
                    {!isStudyMaterial ? (
                      <>
                        <h3
                          style={{ color: displayAccentColor }}
                          className={`text-lg font-bold mb-4 ${montserrat.className}`}>
                          Enroll / Purchase
                        </h3>

                        {/* One-time payment option */}
                        <div
                          className={`mb-4 p-4 border rounded-lg cursor-pointer transition-all min-h-[80px] flex flex-col justify-center ${
                            paymentType === 'onetime' ? '' : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                          }`}
                          style={{
                            borderColor: paymentType === 'onetime' ? displayAccentColor : undefined,
                            ...(paymentType === 'onetime' ? getAccentGradient() : {}),
                          }}
                          onClick={() => setPaymentType('onetime')}>
                          <div className="flex items-center mb-2">
                            <div
                              className="w-4 h-4 rounded-full mr-3"
                              style={{
                                backgroundColor: paymentType === 'onetime' ? displayAccentColor : 'transparent',
                                border: paymentType === 'onetime' ? 'none' : '2px solid #d1d5db',
                              }}></div>
                            <span className={`font-bold text-gray-900 ${montserrat.className}`}>One-time payment</span>
                          </div>
                          <div
                            className="text-sm text-gray-900 font-semibold ml-7"
                            dangerouslySetInnerHTML={{
                              __html: dynamicText.savingsText,
                            }}
                          />
                        </div>

                        {/* Payment plan option */}
                        <div
                          className={`mb-1 p-4 border rounded-lg cursor-pointer transition-all min-h-[80px] flex flex-col justify-center ${
                            paymentType === 'plan' ? '' : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                          }`}
                          style={{
                            borderColor: paymentType === 'plan' ? displayAccentColor : undefined,
                            ...(paymentType === 'plan' ? getAccentGradient() : {}),
                          }}
                          onClick={() => setPaymentType('plan')}>
                          <div className="flex items-center mb-2">
                            <div
                              className="w-4 h-4 rounded-full mr-3"
                              style={{
                                backgroundColor: paymentType === 'plan' ? displayAccentColor : 'transparent',
                                border: paymentType === 'plan' ? 'none' : '2px solid #d1d5db',
                              }}></div>
                            <span className={`font-bold text-gray-900 ${montserrat.className}`}>Payment plan</span>
                          </div>
                          <div className="text-sm text-gray-900 font-semibold ml-7">
                            Start today, pay over 12 months
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="h-full"></div>
                    )}
                  </div>

                  {/* Purchase Buttons */}
                  {Object.entries(planConfigs).map(([key, plan]) => (
                    <div
                      key={key}
                      className={`bg-white rounded-b-2xl border-radius-bottom ${
                        key === 'mvp'
                          ? 'border-l-4 border-r-4 border-b-4'
                          : 'border-l border-r border-b border-gray-200'
                      } p-6 flex flex-col justify-end`}
                      style={{
                        borderColor: key === 'mvp' ? displayAccentColor : undefined,
                        ...(key === 'mvp' ? getMvpShadowStyle() : {}),
                      }}>
                      {/* Flash Sale Banner for MVP */}
                      {key === 'mvp' && (
                        <div className="mb-3 flex justify-center">
                          <div
                            className="relative text-white rounded-xl py-4 text-center overflow-hidden w-full flex items-center justify-center cursor-pointer transition-all duration-300 hover:opacity-90 flash-sale-wiggle"
                            style={{
                              background: `linear-gradient(135deg, ${displayAccentColor} 0%, ${displayAccentColor}dd 50%, ${displayAccentColor} 100%)`,
                              minHeight: '60px',
                            }}
                            onClick={() => handlePurchase(plan.planId)}>
                            {/* Animated background sparkles */}
                            <div className="absolute inset-0 opacity-20">
                              <div className="absolute top-2 left-4 w-1 h-1 bg-white rounded-full animate-pulse"></div>
                              <div className="absolute top-4 right-6 w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                              <div
                                className="absolute bottom-3 left-8 w-1 h-1 bg-white rounded-full animate-pulse"
                                style={{ animationDelay: '0.5s' }}></div>
                              <div
                                className="absolute bottom-5 right-4 w-1 h-1 bg-white rounded-full animate-pulse"
                                style={{ animationDelay: '1s' }}></div>
                            </div>

                            {/* Sale text - perfectly centered */}
                            <div className="relative z-10 flex items-center justify-center h-full w-full text-center">
                              <div className="text-sm font-black tracking-tight text-center w-full flex items-center justify-center">
                                <span className="block text-center mx-auto">
                                  {isStudyMaterial
                                    ? dynamicText.flashSaleText
                                    : paymentType === 'onetime'
                                    ? dynamicText.flashSaleText
                                    : dynamicText.installmentText}
                                </span>
                              </div>
                            </div>

                            {/* Subtle pulse effect border */}
                            <div className="absolute inset-0 rounded-xl border-2 border-white opacity-30"></div>
                          </div>
                        </div>
                      )}

                      {/* Final Pricing Display */}
                      <div className="mb-4">
                        {isStudyMaterial ? (
                          key === 'mvp' ? (
                            <div className="bg-gray-50 rounded-2xl min-h-[94px]">
                              <div className="grid grid-cols-2 gap-2 h-full items-stretch">
                                <button
                                  type="button"
                                  className="p-2 rounded-2xl transition-all text-left border-2 bg-gray-100 h-full"
                                  style={{
                                    borderColor: paymentType === 'plan' ? displayAccentColor : 'transparent',
                                    boxShadow: paymentType === 'plan' ? '0 0 0 1px rgba(0,0,0,0.02)' : undefined,
                                  }}
                                  onClick={() => setPaymentType('plan')}>
                                  <div className="text-gray-400 line-through text-lg flex justify-center items-center">
                                    ${monthlySubscriptionPrices?.gold?.amount ?? '--'}
                                  </div>
                                  <div
                                    className={`text-2xl font-black text-gray-900 ${montserrat.className} flex justify-center items-center`}>
                                    $
                                    {monthlySubscriptionPrices?.gold
                                      ? monthlySubscriptionPrices.gold.amount -
                                        (monthlySubscriptionPrices.gold.defaultCoupon?.discountAmount || 0)
                                      : '--'}
                                  </div>
                                  <div className="text-xs text-gray-700 font-semibold flex justify-center items-center">
                                    per month
                                  </div>
                                </button>

                                <button
                                  type="button"
                                  className="p-2 rounded-2xl transition-all text-left border-2 bg-gray-100 h-full"
                                  style={{
                                    borderColor: paymentType === 'onetime' ? displayAccentColor : 'transparent',
                                    boxShadow: paymentType === 'onetime' ? '0 0 0 1px rgba(0,0,0,0.02)' : undefined,
                                  }}
                                  onClick={() => setPaymentType('onetime')}>
                                  <div className="text-gray-400 line-through text-lg font-bold flex justify-center items-center">
                                    ${purchasePrices?.gold?.amount ?? '--'}
                                  </div>
                                  <div
                                    className={`text-2xl font-black text-gray-900 ${montserrat.className} flex justify-center items-center`}>
                                    $
                                    {purchasePrices?.gold
                                      ? purchasePrices.gold.amount -
                                        (purchasePrices.gold.defaultCoupon?.discountAmount || 0)
                                      : '--'}
                                  </div>
                                  <div className="text-xs text-gray-700 font-semibold flex justify-center items-center">
                                    lifetime
                                  </div>
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="p-4 bg-gray-100 rounded-2xl text-center min-h-[94px] flex flex-col justify-center">
                              <div className={`text-2xl font-black text-gray-900 ${montserrat.className}`}>
                                ${plan.pricing.monthly}
                              </div>
                              <div className="text-sm text-gray-800 font-semibold">per month</div>
                            </div>
                          )
                        ) : (
                          <div className="text-center">
                            {paymentType === 'onetime' ? (
                              <>
                                {plan.pricing.original && (
                                  <div className="h-7 flex items-center justify-center">
                                    <div className="text-gray-400 line-through text-xl">${plan.pricing.original}</div>
                                  </div>
                                )}
                                {!plan.pricing.original && <div className="h-7"></div>}
                                <div className={`text-4xl font-black text-gray-900 ${montserrat.className}`}>
                                  ${plan.pricing.sale}
                                </div>
                                <div className={`text-gray-800 font-semibold ${montserrat.className}`}>One-time</div>
                              </>
                            ) : plan.pricing.monthly ? (
                              <>
                                {/* Consistent height for crossed-out prices */}
                                <div className="h-7 flex items-center justify-center">
                                  {key === 'mvp' && installmentPrices?.gold?.amount ? (
                                    <div className="flex items-center w-full">
                                      <div className="flex-1 text-center">
                                        <div className="text-gray-400 line-through text-xl">
                                          ${Math.round(installmentPrices.gold.amount / 12)}
                                        </div>
                                      </div>
                                      <div className="flex-1 text-center">
                                        <div className="text-gray-400 line-through text-xl">
                                          ${installmentPrices.gold.amount}
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div></div>
                                  )}
                                </div>
                                {/* Payment Plan Layout - Monthly and Total in 2 Columns */}
                                <div className="flex items-end w-full mb-2">
                                  {/* Monthly Price Column */}
                                  <div className="flex-1 text-center">
                                    <div className={`text-3xl font-black text-gray-900 ${montserrat.className}`}>
                                      ${plan.pricing.monthly}
                                    </div>
                                    <div className="text-sm text-gray-800 font-semibold">monthly</div>
                                  </div>

                                  {/* Total Price Column */}
                                  <div className="flex-1 text-center">
                                    <div className={`text-3xl font-black text-gray-900 ${montserrat.className}`}>
                                      $
                                      {key === 'mvp' && mvpInstallmentPricing
                                        ? mvpInstallmentPricing?.totalAfterAllCoupons
                                        : plan.pricing.installmentTotal}
                                    </div>
                                    <div className="text-sm text-gray-800 font-semibold">12 months</div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="h-7"></div>
                                <div className="text-4xl font-black text-gray-400">N/A</div>
                                <div className="h-6"></div>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Purchase Button */}
                      <button
                        className={`w-full py-3 rounded-lg font-bold transition-all duration-300 ${
                          montserrat.className
                        } ${
                          plan.pricing.monthly || selectedPaymentType === 'onetime'
                            ? 'text-white hover:opacity-90'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                        style={{
                          backgroundColor:
                            plan.pricing.monthly || selectedPaymentType === 'onetime' ? displayAccentColor : undefined,
                        }}
                        onClick={() =>
                          handlePurchase(plan.planId, isStudyMaterial && key !== 'mvp' ? 'plan' : paymentType)
                        }
                        disabled={!plan.pricing.monthly && selectedPaymentType === 'plan'}>
                        {isStudyMaterial
                          ? key === 'mvp'
                            ? selectedPaymentType === 'onetime'
                              ? 'Purchase'
                              : 'Enroll'
                            : 'Enroll'
                          : paymentType === 'plan' && key === 'mvp'
                          ? `Start for $${getStartForPrice()}`
                          : 'Purchase'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Layout - Tab Interface */}
          <div className="lg:hidden">
            {/* Plan Tabs */}
            <div
              className={`transition-all duration-200 ease-out ${
                isTabsSticky ? 'fixed left-0 right-0 z-40 bg-white border-b border-gray-200 shadow-lg' : 'relative'
              }`}
              style={
                isTabsSticky
                  ? {
                      top: '64px',
                      paddingTop: '12px',
                      paddingBottom: '6px',
                      transform: 'translate3d(0, 0, 0)', // Force hardware acceleration with better performance
                      willChange: 'transform',
                      backfaceVisibility: 'hidden', // Prevent flickering on some devices
                      perspective: '1000px', // Improve 3D rendering
                    }
                  : {
                      transform: 'translate3d(0, 0, 0)', // Keep hardware acceleration even when not sticky
                      backfaceVisibility: 'hidden',
                    }
              }>
              <div className={`flex gap-2 bg-white rounded-lg p-1 shadow-sm ${isTabsSticky ? 'mx-4 mb-0' : 'mb-6'}`}>
                {Object.entries(planConfigs).map(([key, plan]) => (
                  <button
                    key={key}
                    className={`flex-1 py-3 px-2 rounded-md text-sm font-bold transition-all duration-200 ${
                      selectedPlan === key
                        ? 'text-white shadow-sm transform scale-105'
                        : 'text-gray-700 hover:text-gray-900 font-semibold bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md transform hover:scale-[1.02] active:scale-[0.98]'
                    }`}
                    style={{
                      backgroundColor: selectedPlan === key ? displayAccentColor : undefined,
                    }}
                    onClick={() => setSelectedPlan(key as keyof PlanConfigs)}>
                    {plan.name}
                    {plan.badge && <div className="text-xs mt-1 opacity-90">{plan.badge}</div>}
                  </button>
                ))}
              </div>
            </div>

            {/* Placeholder for tabs when sticky - maintains exact layout space */}
            <div
              className={`transition-all duration-200 ease-out ${isTabsSticky ? 'block' : 'hidden'}`}
              style={{
                // Match exact height: paddingTop(12px) + paddingBottom(6px) + button height + border + margin
                height: '82px', // 12px + 6px + ~52px (button) + ~6px (border/spacing) + ~6px (rounded adjustment)
                transform: 'translate3d(0, 0, 0)',
                backfaceVisibility: 'hidden',
              }}></div>

            {/* Selected Plan Details */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 min-h-[600px]">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-3">
                  <LottiePlayer
                    key={selectedPlan}
                    src={planConfigs[selectedPlan].lottie}
                    width={120}
                    height={120}
                    lottieAnimations={lottieAnimationUrls}
                  />
                </div>
                <h3 className={`text-2xl font-black text-gray-900 mt-2 ${montserrat.className}`}>
                  {planConfigs[selectedPlan].name}
                </h3>
              </div>

              {/* Payment Type Selection */}
              <div className="mb-6">
                <h4 style={{ color: displayAccentColor }} className={`text-lg font-bold mb-4 ${montserrat.className}`}>
                  Choose Payment Type
                </h4>

                {isStudyMaterial && selectedPlan === 'mvp' ? (
                  <div className="p-2 bg-gray-50 rounded-2xl border border-gray-200">
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        className={`p-4 rounded-2xl transition-all text-left border-2 ${
                          paymentType === 'plan' ? 'bg-white' : 'bg-gray-100'
                        }`}
                        style={{
                          borderColor: paymentType === 'plan' ? displayAccentColor : 'transparent',
                        }}
                        onClick={() => setPaymentType('plan')}>
                        <div className="text-gray-400 line-through text-lg font-bold">
                          ${monthlySubscriptionPrices?.gold?.amount ?? '--'}
                        </div>
                        <div className={`text-3xl font-black text-gray-900 ${montserrat.className}`}>
                          $
                          {monthlySubscriptionPrices?.gold
                            ? monthlySubscriptionPrices.gold.amount -
                              (monthlySubscriptionPrices.gold.defaultCoupon?.discountAmount || 0)
                            : '--'}
                        </div>
                        <div className="text-gray-700 font-semibold">per month</div>
                      </button>

                      <button
                        type="button"
                        className={`p-4 rounded-2xl transition-all text-left border-2 ${
                          paymentType === 'onetime' ? 'bg-white' : 'bg-gray-100'
                        }`}
                        style={{
                          borderColor: paymentType === 'onetime' ? displayAccentColor : 'transparent',
                        }}
                        onClick={() => setPaymentType('onetime')}>
                        <div className="text-gray-400 line-through text-lg font-bold">
                          ${purchasePrices?.gold?.amount ?? '--'}
                        </div>
                        <div className={`text-3xl font-black text-gray-900 ${montserrat.className}`}>
                          $
                          {purchasePrices?.gold
                            ? purchasePrices.gold.amount - (purchasePrices.gold.defaultCoupon?.discountAmount || 0)
                            : '--'}
                        </div>
                        <div className="text-gray-700 font-semibold">lifetime</div>
                      </button>
                    </div>
                  </div>
                ) : isStudyMaterial && selectedPlan !== 'mvp' ? (
                  <div className="p-2 bg-gray-50 rounded-2xl border border-gray-200">
                    <div className="text-sm font-bold text-gray-900 mb-3">Subscription Plan</div>
                    <div className="p-4 rounded-2xl bg-white border-2" style={{ borderColor: displayAccentColor }}>
                      <div className={`text-3xl font-black text-gray-900 ${montserrat.className}`}>
                        ${planConfigs[selectedPlan].pricing.monthly}
                      </div>
                      <div className="text-gray-700 font-semibold">per month</div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      className={`mb-4 p-3 border rounded-lg cursor-pointer transition-all ${
                        paymentType === 'onetime' ? '' : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                      }`}
                      style={{
                        borderColor: paymentType === 'onetime' ? displayAccentColor : undefined,
                        ...(paymentType === 'onetime' ? getAccentGradient() : {}),
                      }}
                      onClick={() => setPaymentType('onetime')}>
                      <div className="flex items-center mb-2">
                        <div
                          className="w-4 h-4 rounded-full mr-3"
                          style={{
                            backgroundColor: paymentType === 'onetime' ? displayAccentColor : 'transparent',
                            border: paymentType === 'onetime' ? 'none' : '2px solid #d1d5db',
                          }}></div>
                        <span className={`font-bold text-gray-900 ${montserrat.className}`}>One-time payment</span>
                      </div>
                      <div
                        className="text-sm text-gray-600 ml-7"
                        dangerouslySetInnerHTML={{
                          __html: dynamicText.savingsText,
                        }}
                      />
                    </div>

                    <div
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        paymentType === 'plan' ? '' : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                      }`}
                      style={{
                        borderColor: paymentType === 'plan' ? displayAccentColor : undefined,
                        ...(paymentType === 'plan' ? getAccentGradient() : {}),
                      }}
                      onClick={() => {
                        setPaymentType('plan');
                        // If current selected plan doesn't support payment plans, switch to MVP
                        if (!planConfigs[selectedPlan].pricing.monthly) {
                          setSelectedPlan('mvp');
                        }
                      }}>
                      <div className="flex items-center mb-2">
                        <div
                          className="w-4 h-4 rounded-full mr-3"
                          style={{
                            backgroundColor: paymentType === 'plan' ? displayAccentColor : 'transparent',
                            border: paymentType === 'plan' ? 'none' : '2px solid #d1d5db',
                          }}></div>
                        <span className={`font-bold text-gray-900 ${montserrat.className}`}>Payment plan</span>
                      </div>
                      <div className="text-sm text-gray-600 ml-7">
                        Pay over a 12-month time frame
                        {!planConfigs[selectedPlan].pricing.monthly && (
                          <span className="block text-xs text-gray-500 mt-1">(Only available with MVP plan)</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Pricing */}
              <div className="text-center mb-6 p-4 bg-gray-50 rounded-lg min-h-[120px] flex flex-col justify-center">
                {(isStudyMaterial ? selectedPaymentType === 'onetime' : paymentType === 'onetime') ? (
                  <>
                    {planConfigs[selectedPlan].pricing.original && (
                      <div className="text-gray-400 line-through text-lg">
                        ${planConfigs[selectedPlan].pricing.original}
                      </div>
                    )}
                    <div className={`text-4xl font-black text-gray-900 ${montserrat.className}`}>
                      ${planConfigs[selectedPlan].pricing.sale}
                    </div>
                    <div className={`text-gray-900 font-semibold ${montserrat.className}`}>
                      {isStudyMaterial ? oneTimePaymentLabel : 'One-time payment'}
                    </div>
                  </>
                ) : planConfigs[selectedPlan].pricing.monthly ? (
                  <>
                    {isStudyMaterial ? (
                      <>
                        <div className="h-6 flex items-center justify-center mb-1">
                          {selectedPlan === 'mvp' && monthlySubscriptionPrices?.gold?.defaultCoupon ? (
                            <div className="text-gray-400 line-through text-lg">
                              ${monthlySubscriptionPrices.gold.amount}
                            </div>
                          ) : (
                            <div></div>
                          )}
                        </div>
                        <div className={`text-3xl font-black text-gray-900 ${montserrat.className}`}>
                          ${planConfigs[selectedPlan].pricing.monthly}
                        </div>
                        <div className="text-sm text-gray-900 font-semibold">per month</div>
                      </>
                    ) : (
                      <>
                        {/* Consistent height for crossed-out prices */}
                        <div className="h-6 flex items-center justify-center mb-1">
                          {selectedPlan === 'mvp' && installmentPrices?.gold?.amount ? (
                            <div className="flex items-end w-full gap-4">
                              <div className="flex-1 text-center">
                                <div className="text-gray-400 line-through text-lg">
                                  ${Math.round(installmentPrices.gold.amount / 12)}
                                </div>
                              </div>
                              <div className="flex-1 text-center">
                                <div className="text-gray-400 line-through text-lg">
                                  ${installmentPrices.gold.amount}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div></div>
                          )}
                        </div>

                        {/* Payment Plan Layout - Monthly and Total in 2 Columns */}
                        <div className="flex items-end w-full mb-2 gap-4">
                          {/* Monthly Price Column */}
                          <div className="flex-1 text-center">
                            <div className={`text-3xl font-black text-gray-900 ${montserrat.className}`}>
                              ${planConfigs[selectedPlan].pricing.monthly}
                            </div>
                            <div className="text-sm text-gray-900 font-semibold">per month</div>
                          </div>

                          {/* Total Price Column */}
                          <div className="flex-1 text-center">
                            <div className={`text-3xl font-black text-gray-900 ${montserrat.className}`}>
                              $
                              {selectedPlan === 'mvp' && mvpInstallmentPricing
                                ? mvpInstallmentPricing?.totalAfterAllCoupons
                                : planConfigs[selectedPlan].pricing.installmentTotal}
                            </div>
                            <div className="text-sm text-gray-900 font-semibold">total (12 months)</div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-2xl font-black text-gray-400">Not Available</div>
                )}
              </div>

              {/* Flash Sale Banner for MVP Mobile */}
              {selectedPlan === 'mvp' && (
                <div className="mb-6">
                  <div
                    className="relative text-white rounded-lg p-3 text-center overflow-hidden border-l-4 cursor-pointer transition-all duration-300 hover:opacity-90 flash-sale-wiggle"
                    style={{
                      background: `linear-gradient(90deg, ${displayAccentColor}15 0%, ${displayAccentColor}08 100%)`,
                      borderLeftColor: displayAccentColor,
                      borderWidth: '0 0 0 4px',
                    }}
                    onClick={() => handlePurchase(planConfigs[selectedPlan].planId, selectedPaymentType)}>
                    {/* Subtle background pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-1 right-3 w-1 h-1 bg-current rounded-full animate-pulse"></div>
                      <div
                        className="absolute bottom-1 left-3 w-1 h-1 bg-current rounded-full animate-pulse"
                        style={{ animationDelay: '1s' }}></div>
                    </div>

                    {/* Sale content - more banner-like */}
                    <div className="flex items-center justify-center">
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        style={{ color: displayAccentColor }}>
                        <path
                          fillRule="evenodd"
                          d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div className="text-sm font-bold text-gray-800">
                        {isStudyMaterial
                          ? dynamicText.flashSaleText
                          : paymentType === 'onetime'
                          ? dynamicText.flashSaleText
                          : dynamicText.installmentText}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Purchase Button */}
              <button
                className={`w-full py-4 rounded-lg font-bold text-lg transition-all duration-300 mb-6 ${
                  montserrat.className
                } ${
                  planConfigs[selectedPlan].pricing.monthly || selectedPaymentType === 'onetime'
                    ? 'text-white hover:opacity-90'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                style={{
                  backgroundColor:
                    planConfigs[selectedPlan].pricing.monthly || selectedPaymentType === 'onetime'
                      ? displayAccentColor
                      : undefined,
                }}
                onClick={() => handlePurchase(planConfigs[selectedPlan].planId, selectedPaymentType)}
                disabled={!planConfigs[selectedPlan].pricing.monthly && selectedPaymentType === 'plan'}>
                {isStudyMaterial
                  ? selectedPlan === 'mvp'
                    ? selectedPaymentType === 'onetime'
                      ? 'Purchase'
                      : 'Enroll'
                    : 'Enroll'
                  : selectedPaymentType === 'plan' && selectedPlan === 'mvp'
                  ? `Start for $${getStartForPrice()}`
                  : 'Purchase'}
              </button>

              {/* Features List */}
              <div className="border-t pt-6">
                {headerContent.showBogoFeatureNote && (
                  <div className="text-sm text-gray-600 mb-4 p-3 bg-blue-50 rounded-lg font-medium text-center">
                    The following study features apply to both the IPTA CPT and Nutrition Academy CNS certifications
                  </div>
                )}
                {/* Thumb Animation with Text */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 mr-4">
                      <LottiePlayer src="thumb" width={40} height={40} lottieAnimations={lottieAnimationUrls} />
                    </div>
                    <div className="flex-1">
                      <div
                        style={{
                          color: displayAccentColor,
                          textDecoration: 'underline dashed',
                          textDecorationColor: displayAccentColor,
                          textDecorationThickness: '1px',
                          textUnderlineOffset: '4px',
                          WebkitTextDecorationLine: 'underline',
                          WebkitTextDecorationColor: displayAccentColor,
                          WebkitTextDecorationStyle: 'dashed',
                        }}
                        className="text-sm font-bold leading-tight cursor-pointer">
                        Click any feature below to learn more about it!
                      </div>
                    </div>
                  </div>
                </div>

                <h4 className={`text-lg font-bold text-gray-800 mb-4 ${montserrat.className}`}>What's Included:</h4>

                {/* Main Features */}
                <div className="space-y-3 mb-6">
                  {featuresList.map((feature, index) => {
                    const isIncluded = planConfigs[selectedPlan].features[index];
                    const accentBg = getAccentBackground();

                    return (
                      <div
                        key={index}
                        className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                          isIncluded ? 'border' : 'bg-gray-50 border border-gray-200 opacity-60'
                        }`}
                        style={
                          isIncluded
                            ? {
                                backgroundColor: accentBg.backgroundColor,
                                borderColor: accentBg.borderColor,
                              }
                            : {}
                        }
                        onClick={() => handleFeatureClick(feature)}>
                        <div className="mr-3">
                          {isIncluded ? (
                            <div
                              className="w-5 h-5 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: displayAccentColor }}>
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                        <span className="text-sm font-medium text-gray-800">{feature}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Bonus Features */}
                <div className="border-t pt-4">
                  <h5 className={`text-md font-bold text-gray-700 mb-3 flex items-center ${montserrat.className}`}>
                    <div className="flex justify-center mr-3">
                      <LottiePlayer src="gift" width={40} height={40} lottieAnimations={lottieAnimationUrls} />
                    </div>
                    Bonus Features for {bonusDate}:
                  </h5>
                  <div className="space-y-3">
                    {visibleBonusesList.map((bonus, index) => {
                      const isIncluded = planConfigs[selectedPlan].bonuses[index];
                      const accentBg = getAccentBackground();

                      return (
                        <div
                          key={index}
                          className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                            isIncluded ? 'border' : 'bg-gray-50 border border-gray-200 opacity-60'
                          }`}
                          style={
                            isIncluded
                              ? {
                                  backgroundColor: accentBg.backgroundColor,
                                  borderColor: accentBg.borderColor,
                                }
                              : {}
                          }
                          onClick={() => handleFeatureClick(bonus)}>
                          <div className="mr-3">
                            {isIncluded ? (
                              <div
                                className="w-5 h-5 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: displayAccentColor }}>
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            ) : (
                              <div className="w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            )}
                          </div>
                          <span className="text-sm font-medium text-gray-800 flex-1">{bonus}</span>
                          {isIncluded &&
                            bonusClaimedData[bonus] &&
                            (bonusClaimedData[bonus].isMessage ? (
                              <span className="text-gray-600 px-3 py-1 rounded text-xs font-semibold ml-2 border border-gray-300 bg-gray-100">
                                {bonusClaimedData[bonus].text}
                              </span>
                            ) : (
                              <span
                                className={`text-white px-3 py-2 rounded text-sm font-bold ml-2 h-6 flex items-center justify-center ${
                                  bonusClaimedData[bonus].color === 'green'
                                    ? 'bg-green-500'
                                    : bonusClaimedData[bonus].color === 'yellow'
                                    ? 'bg-yellow-500'
                                    : 'bg-red-500'
                                }`}>
                                {bonusClaimedData[bonus].text}
                              </span>
                            ))}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Mobile Purchase Button */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <button
                    className={`w-full py-4 px-6 rounded-lg text-lg font-bold transition-all duration-300 ${
                      planConfigs[selectedPlan].pricing.monthly || selectedPaymentType === 'onetime'
                        ? 'text-white hover:opacity-90'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    style={{
                      backgroundColor:
                        planConfigs[selectedPlan].pricing.monthly || selectedPaymentType === 'onetime'
                          ? displayAccentColor
                          : undefined,
                    }}
                    onClick={() => handlePurchase(planConfigs[selectedPlan].planId, selectedPaymentType)}
                    disabled={!planConfigs[selectedPlan].pricing.monthly && selectedPaymentType === 'plan'}>
                    {isStudyMaterial
                      ? selectedPlan === 'mvp'
                        ? selectedPaymentType === 'onetime'
                          ? 'Purchase'
                          : 'Enroll'
                        : 'Enroll'
                      : selectedPaymentType === 'onetime'
                      ? `Get Started - $${planConfigs[selectedPlan].pricing.sale}`
                      : planConfigs[selectedPlan].pricing.monthly
                      ? selectedPlan === 'mvp' && selectedPaymentType === 'plan'
                        ? `Start for $${getStartForPrice()}`
                        : `Start for $${planConfigs[selectedPlan].pricing.monthly}`
                      : 'Not Available'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Feature Modal */}
      {selectedFeature && featureData[selectedFeature] && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedFeature(null)}>
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center">
                  {featureData[selectedFeature].icon && (
                    <div className="w-12 h-12 mr-4 flex items-center justify-center">
                      <img
                        src={featureData[selectedFeature].icon}
                        alt={featureData[selectedFeature].title}
                        className="w-10 h-10 object-contain"
                        onError={(e) => {
                          // Replace with a fallback icon
                          const target = e.currentTarget as HTMLImageElement;
                          target.src =
                            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iIzFFQ0JGMyIvPgo8cGF0aCBkPSJNMjAgMTBMMjMuMDkgMTYuMjZMMzAgMTdMMjUgMjJMMjYuMTggMjlMMjAgMjVMMTMuODIgMjlMMTUgMjJMMTAgMTdMMTYuOTEgMTYuMjZMMjAgMTBaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
                          target.style.opacity = '0.7';
                        }}
                      />
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-gray-900">{featureData[selectedFeature].title}</h3>
                </div>
                <button
                  onClick={() => setSelectedFeature(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p
                className="text-gray-900 leading-relaxed text-lg mb-6 font-medium"
                dangerouslySetInnerHTML={{
                  __html: featureData[selectedFeature].description,
                }}
              />

              {/* Media Content - Using 16:9 aspect ratio for both videos and images */}
              <div className="mb-6">
                {featureData[selectedFeature].mediaType === 'video' ? (
                  <div className="relative w-full aspect-video rounded-lg shadow-lg overflow-hidden bg-gray-100">
                    <video
                      className="w-full h-full object-cover"
                      muted
                      autoPlay
                      playsInline
                      loop
                      onError={(e) => {
                        // Hide the video and show a fallback message
                        const target = e.currentTarget as HTMLVideoElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = `
                            <div class="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600">
                              <div class="text-center p-8">
                                <div class="text-4xl mb-2">🎥</div>
                                <div class="font-semibold">Video preview unavailable</div>
                                <div class="text-sm mt-1">Content will be available in your course</div>
                              </div>
                            </div>
                          `;
                        }
                      }}>
                      <source src={featureData[selectedFeature].mediaUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ) : (
                  <div className="w-full aspect-video rounded-lg shadow-lg overflow-hidden bg-gray-100">
                    <img
                      src={featureData[selectedFeature].mediaUrl}
                      alt={featureData[selectedFeature].title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Replace with a fallback image
                        const target = e.currentTarget as HTMLImageElement;
                        target.src =
                          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIyNSIgdmlld0JveD0iMCAwIDQwMCAyMjUiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMjI1IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgODBMMjIwIDEyMEwxODAgMTIwWiIgZmlsbD0iIzlDQTNBRiIvPgo8dGV4dCB4PSIyMDAiIHk9IjE1MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzZCNzI4MCIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiPkltYWdlIHVuYXZhaWxhYmxlPC90ZXh0Pgo8dGV4dCB4PSIyMDAiIHk9IjE2OCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzlDQTNBRiIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTIiPkNvbnRlbnQgYXZhaWxhYmxlIGluIGNvdXJzZTwvdGV4dD4KPHN2Zz4K';
                        target.className = 'w-full h-full object-cover opacity-70';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
